// src/pages/Vouchers/VouchersListPage.jsx
// قائمة السندات — بالتصميم الموحّد للتقارير

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import {
  Box, Typography, TextField, IconButton, Tooltip, Stack, Autocomplete,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Menu, MenuItem, ListItemIcon,
} from "@mui/material";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import SouthWestIcon from "@mui/icons-material/SouthWest";
import NorthEastIcon from "@mui/icons-material/NorthEast";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddIcon from "@mui/icons-material/Add";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import ArrowCircleDownOutlinedIcon from "@mui/icons-material/ArrowCircleDownOutlined";
import ArrowCircleUpOutlinedIcon from "@mui/icons-material/ArrowCircleUpOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import dayjs from "dayjs";

import axios from "../../axios";
import VoucherPrintDialog from "./VoucherPrintDialog";
import { useAuth } from "../../context/AuthContext";
import { PERMISSIONS } from "../../constants/permissions";
import {
  ReportPage, ReportFilters, ReportSelect, ReportTableCard,
  useReportDateRange, reportGridSx, fieldSx, fmtMoney, fmtNumber,
} from "../../components/Reports/ReportKit";

// ───────────────────────── أنواع السندات ─────────────────────────
const RECEIPT_TYPES = [101, 103];
const PAYMENT_TYPES = [102, 104, 105];

const VOUCHER_TYPES = [
  { value: 101, label: "سند قبض من عميل", route: "customer-receipt", icon: <PersonOutlineIcon />, bg: "#d1fae5", fg: "#047857" },
  { value: 103, label: "سند قبض عام", route: "general-receipt", icon: <ArrowCircleDownOutlinedIcon />, bg: "#e0e7ff", fg: "#4338ca" },
  { value: 102, label: "سند صرف لمورد", route: "supplier-payment", icon: <LocalShippingOutlinedIcon />, bg: "#ffe4e6", fg: "#be123c" },
  { value: 104, label: "سند صرف عام", route: "general-payment", icon: <ArrowCircleUpOutlinedIcon />, bg: "#fef3c7", fg: "#b45309" },
  { value: 105, label: "سند صرف ضريبة", route: "tax-payment", icon: <AccountBalanceOutlinedIcon />, bg: "#fef3c7", fg: "#b45309" },
];
const TYPE_BY_ID = Object.fromEntries(VOUCHER_TYPES.map(t => [t.value, t]));

const PAYMENT_OPTIONS = [{ value: "1", label: "نقدي" }, { value: "2", label: "بنكي" }];

const isReceipt = (t) => RECEIPT_TYPES.includes(Number(t));
const isPayment = (t) => PAYMENT_TYPES.includes(Number(t));

export default function VouchersListPage() {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const companyId = parseInt(localStorage.getItem("companyId"), 10);

  const range = useReportDateRange("today");
  const [voucherType, setVoucherType] = useState("");
  const [paymentType, setPaymentType] = useState("");
  const [customer, setCustomer] = useState(null);
  const [supplier, setSupplier] = useState(null);
  const [minAmount, setMinAmount] = useState("");
  const [maxAmount, setMaxAmount] = useState("");
  const [searchText, setSearchText] = useState("");

  const [customers, setCustomers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [companyInfo, setCompanyInfo] = useState(null);

  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [printVoucher, setPrintVoucher] = useState(null);
  const [newMenuAnchor, setNewMenuAnchor] = useState(null);

  // ── القوائم المساعدة (مرة واحدة) ──
  useEffect(() => {
    if (!companyId) return;
    axios.get(`/Customer/by-company/${companyId}`).then(r => setCustomers(r.data || [])).catch(() => {});
    axios.get(`/Supplier/by-company/${companyId}`).then(r => setSuppliers(r.data || [])).catch(() => {});
    axios.get(`/CompanyProfile/public/${companyId}`)
      .then(({ data: d }) => setCompanyInfo({
        name: d.nameAr, nameEn: d.nameEn, taxNumber: d.vatNo, address: d.address,
        phone: d.phone, email: d.email, logoUrl: d.logoUrl,
      }))
      .catch(() => {});
  }, [companyId]);

  // ── جلب السندات ──
  const fetchVouchers = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const params = {
        companyId,
        fromDate: range.dateFrom,
        toDate: range.dateTo,
        ...(voucherType && { voucherType: Number(voucherType) }),
        ...(paymentType && { paymentType: Number(paymentType) }),
        ...(customer && { customerId: Number(customer.id) }),
        ...(supplier && { supplierId: Number(supplier.id) }),
        ...(minAmount !== "" && { minAmount: Number(minAmount) }),
        ...(maxAmount !== "" && { maxAmount: Number(maxAmount) }),
        ...(searchText.trim() && { searchText: searchText.trim() }),
      };
      const res = await axios.get("/Vouchers", { params });
      const data = res.data?.data || res.data?.vouchers || res.data || [];
      setVouchers(Array.isArray(data)
        ? data.map((v, i) => ({
            ...v,
            id: v.id || `temp-${i}`,
            voucherNumber: v.voucherNumber || "—",
            voucherTypeName: v.voucherTypeName || TYPE_BY_ID[v.voucherType]?.label || "غير محدد",
            amount: Number(v.amount) || 0,
          }))
        : []);
    } catch (error) {
      toast.error(`فشل في جلب السندات: ${error.response?.data?.message || error.message}`);
      setVouchers([]);
    } finally {
      setLoading(false);
    }
  }, [companyId, range.dateFrom, range.dateTo, voucherType, paymentType, customer, supplier, minAmount, maxAmount, searchText]);

  // ✅ جلب واحد مع تأخير بسيط عند الكتابة (سابقًا كان يُجلب مرتين عند فتح الصفحة)
  useEffect(() => {
    const id = setTimeout(fetchVouchers, 400);
    return () => clearTimeout(id);
  }, [fetchVouchers]);

  // ✅ الإجماليات من السندات المعروضة — تطابق الجدول مع أي فلتر
  const totals = useMemo(() => {
    let receipts = 0, payments = 0, cash = 0, bank = 0;
    for (const v of vouchers) {
      if (isReceipt(v.voucherType)) receipts += v.amount;
      else if (isPayment(v.voucherType)) payments += v.amount;
      const signed = isReceipt(v.voucherType) ? v.amount : -v.amount;
      if (Number(v.paymentType) === 1) cash += signed;
      if (Number(v.paymentType) === 2) bank += signed;
    }
    return { receipts, payments, net: receipts - payments, cash, bank };
  }, [vouchers]);

  // ── الصلاحيات ──
  const perms = (type) => {
    const r = isReceipt(type), p = isPayment(type);
    return {
      view: r ? hasPermission(PERMISSIONS.BOND_RECEIPT_VIEW) : p ? hasPermission(PERMISSIONS.BOND_PAYMENT_VIEW) : hasPermission(PERMISSIONS.VOUCHER_VIEW),
      edit: r ? hasPermission(PERMISSIONS.BOND_RECEIPT_EDIT) : p ? hasPermission(PERMISSIONS.BOND_PAYMENT_EDIT) : false,
      print: r ? hasPermission(PERMISSIONS.BOND_RECEIPT_PRINT) : p ? hasPermission(PERMISSIONS.BOND_PAYMENT_PRINT) : false,
      del: r ? hasPermission(PERMISSIONS.BOND_RECEIPT_DELETE) : p ? hasPermission(PERMISSIONS.BOND_PAYMENT_DELETE) : false,
    };
  };

  const openVoucher = (v, mode) => {
    const t = TYPE_BY_ID[v.voucherType];
    if (!t) return toast.error("نوع السند غير معروف");
    navigate(`/vouchers/${t.route}/${v.id}/${mode}`);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`/Vouchers/${deleteTarget.id}`);
      toast.success("تم حذف السند بنجاح");
      setDeleteTarget(null);
      fetchVouchers();
    } catch (error) {
      toast.error(error.response?.data?.message || "فشل في حذف السند");
    } finally {
      setDeleting(false);
    }
  };

  // ── الأعمدة ──
  const columns = useMemo(() => [
    {
      field: "voucherNumber", headerName: "رقم السند", width: 110, headerAlign: "center", align: "center",
      renderCell: (p) => <Typography sx={{ direction: "ltr", fontWeight: 800, fontSize: 13.5, color: "#4f46e5" }}>{p.value}</Typography>,
    },
    {
      field: "voucherTypeName", headerName: "نوع السند", width: 150, headerAlign: "center", align: "center",
      renderCell: (p) => {
        const t = TYPE_BY_ID[p.row.voucherType];
        return (
          <Box sx={{
            display: "inline-flex", alignItems: "center", gap: .5, px: 1.1, py: .3, borderRadius: "999px",
            fontSize: 12, fontWeight: 800, bgcolor: t?.bg ?? "#f1f5f9", color: t?.fg ?? "#475569",
            "& svg": { fontSize: 15 },
          }}>
            {t?.icon}{p.value}
          </Box>
        );
      },
    },
    {
      field: "voucherDate", headerName: "التاريخ", width: 120, headerAlign: "center", align: "center",
      renderCell: (p) => {
        const d = dayjs(p.value);
        return d.isValid() ? (
          <Tooltip title={d.format("YYYY/MM/DD HH:mm")}>
            <Typography sx={{ direction: "ltr", fontSize: 13.5, fontWeight: 600 }}>{d.format("YYYY-MM-DD")}</Typography>
          </Tooltip>
        ) : "—";
      },
    },
    {
      field: "party", headerName: "العميل / المورد", flex: 1, minWidth: 150, headerAlign: "center", align: "center",
      sortable: false,
      renderCell: (p) => {
        const name = p.row.customerName || p.row.supplierName;
        if (!name) return <Typography sx={{ fontSize: 13, color: "#94a3b8" }}>عام</Typography>;
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: .5, "& svg": { fontSize: 16, color: p.row.customerName ? "#0284c7" : "#b45309" } }}>
            {p.row.customerName ? <PersonOutlineIcon /> : <LocalShippingOutlinedIcon />}
            <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>{name}</Typography>
          </Box>
        );
      },
    },
    {
      field: "paymentTypeName", headerName: "الدفع", width: 90, headerAlign: "center", align: "center",
      renderCell: (p) => {
        const t = Number(p.row.paymentType);
        return (
          <Box sx={{ px: 1, py: .25, borderRadius: "8px", fontSize: 12, fontWeight: 700,
            bgcolor: t === 2 ? "#e0f2fe" : "#f1f5f9", color: t === 2 ? "#0369a1" : "#334155" }}>
            {p.row.paymentTypeName || (t === 1 ? "نقدي" : t === 2 ? "بنكي" : "—")}
          </Box>
        );
      },
    },
    {
      field: "amount", headerName: "المبلغ", type: "number", width: 135, headerAlign: "center", align: "center",
      renderCell: (p) => {
        const r = isReceipt(p.row.voucherType);
        return (
          <Typography sx={{ direction: "ltr", fontWeight: 800, fontSize: 14, color: r ? "#047857" : "#be123c" }}>
            {r ? "+" : "−"}{fmtMoney(p.value)}
          </Typography>
        );
      },
    },
    { field: "branchName", headerName: "الفرع", width: 115, headerAlign: "center", align: "center", renderCell: (p) => p.value || "—" },
    {
      field: "description", headerName: "الوصف", flex: 1.2, minWidth: 160, headerAlign: "center",
      renderCell: (p) => {
        const v = p.value || p.row.notes || "";
        return (
          <Tooltip title={v}>
            <Typography sx={{ fontSize: 13, color: v ? "#334155" : "#cbd5e1", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {v || "—"}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: "actions", headerName: "الإجراءات", width: 175, sortable: false, filterable: false, headerAlign: "center", align: "center",
      renderCell: (p) => {
        const can = perms(p.row.voucherType);
        const btn = { width: 32, height: 32, borderRadius: "9px", border: "1px solid #e5e7eb", bgcolor: "#fff" };
        return (
          <Stack direction="row" spacing={.5}>
            {can.view && (
              <Tooltip title="عرض السند">
                <IconButton size="small" sx={{ ...btn, color: "#0284c7" }} onClick={() => openVoucher(p.row, "view")}>
                  <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}
            {p.row.journalEntryGlobalId && (
              <Tooltip title="عرض القيد المحاسبي">
                <IconButton size="small" sx={{ ...btn, color: "#7c3aed" }}
                  onClick={() => window.open(`/journal-entries/${p.row.journalEntryGlobalId}/view`, "_blank")}>
                  <MenuBookOutlinedIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}
            {can.edit && (
              <Tooltip title="تعديل">
                <IconButton size="small" sx={{ ...btn, color: "#4f46e5" }} onClick={() => openVoucher(p.row, "edit")}>
                  <EditOutlinedIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}
            {can.print && (
              <Tooltip title="طباعة">
                <IconButton size="small" sx={{ ...btn, color: "#334155" }} onClick={() => setPrintVoucher(p.row)}>
                  <PrintOutlinedIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}
            {can.del && (
              <Tooltip title="حذف">
                <IconButton size="small" sx={{ ...btn, color: "#e11d48", borderColor: "#fecdd3" }} onClick={() => setDeleteTarget(p.row)}>
                  <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        );
      },
    },

  ], [hasPermission]);

  const resetFilters = () => {
    range.reset();
    setVoucherType("");
    setPaymentType("");
    setCustomer(null);
    setSupplier(null);
    setMinAmount("");
    setMaxAmount("");
    setSearchText("");
  };

  const canCreate = hasPermission(PERMISSIONS.VOUCHER_CREATE);
  const activeCount = [voucherType, paymentType, customer, supplier, minAmount, maxAmount, searchText.trim()].filter(Boolean).length;
  const amountSx = { ...fieldSx, "& input": { direction: "ltr", textAlign: "right" } };

  return (
    <>
      <ReportPage
        title="السندات"
        subtitle="سندات القبض والصرف مع أثرها على التدفق النقدي"
        icon={<ReceiptLongIcon />}
        period={range}
        actions={[
          { label: "تحديث", icon: <RefreshIcon />, onClick: fetchVouchers, disabled: loading },
          ...(canCreate ? [{ label: "سند جديد", icon: <AddIcon />, onClick: (e) => setNewMenuAnchor(e.currentTarget), primary: true }] : []),
        ]}
        stats={[
          { label: "عدد السندات", value: fmtNumber(vouchers.length), icon: <FormatListNumberedIcon />, color: "primary" },
          { label: "إجمالي المقبوضات", value: fmtMoney(totals.receipts), icon: <SouthWestIcon />, color: "success" },
          { label: "إجمالي المدفوعات", value: fmtMoney(totals.payments), icon: <NorthEastIcon />, color: "error" },
          { label: "صافي النقدي", value: fmtMoney(totals.cash), icon: <AccountBalanceWalletIcon />, color: "slate" },
          { label: "صافي البنك", value: fmtMoney(totals.bank), icon: <AccountBalanceOutlinedIcon />, color: "info" },
          { label: "صافي التدفق", value: fmtMoney(totals.net), icon: <AccountBalanceWalletIcon />, color: totals.net < 0 ? "error" : "success", highlight: true },
        ]}
        filters={
          <ReportFilters
            range={range}
            search={searchText}
            onSearchChange={setSearchText}
            searchPlaceholder="رقم السند، الوصف، الملاحظات..."
            onApply={fetchVouchers}
            onReset={resetFilters}
            loading={loading}
            activeCount={activeCount}
          >
            <ReportSelect
              label="نوع السند" value={voucherType} onChange={setVoucherType}
              options={VOUCHER_TYPES.map(t => ({ value: String(t.value), label: t.label }))}
            />
            <ReportSelect label="طريقة الدفع" value={paymentType} onChange={setPaymentType} options={PAYMENT_OPTIONS} />
            <Autocomplete
              size="small" options={customers} value={customer}
              onChange={(_, v) => setCustomer(v)}
              getOptionLabel={(c) => c?.name || ""}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              noOptionsText="لا توجد نتائج"
              renderInput={(params) => <TextField {...params} label="العميل" placeholder="الكل" sx={fieldSx} />}
            />
            <Autocomplete
              size="small" options={suppliers} value={supplier}
              onChange={(_, v) => setSupplier(v)}
              getOptionLabel={(s) => s?.name || ""}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              noOptionsText="لا توجد نتائج"
              renderInput={(params) => <TextField {...params} label="المورد" placeholder="الكل" sx={fieldSx} />}
            />
            <TextField
              size="small" fullWidth type="number" label="من مبلغ" value={minAmount}
              onChange={(e) => setMinAmount(e.target.value)} inputProps={{ min: 0 }} sx={amountSx}
            />
            <TextField
              size="small" fullWidth type="number" label="إلى مبلغ" value={maxAmount}
              onChange={(e) => setMaxAmount(e.target.value)} inputProps={{ min: 0 }} sx={amountSx}
            />
          </ReportFilters>
        }
      >
        <ReportTableCard title="قائمة السندات" count={fmtNumber(vouchers.length)}>
          <DataGrid
            rows={vouchers}
            columns={columns}
            loading={loading}
            slots={{ toolbar: GridToolbar }}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
            pageSizeOptions={[25, 50, 100]}
            disableRowSelectionOnClick
            getRowHeight={() => 52}
            localeText={{
              noRowsLabel: "لا توجد سندات في هذه الفترة",
              toolbarColumns: "الأعمدة",
              toolbarFilters: "الفلاتر",
              toolbarDensity: "الكثافة",
              toolbarExport: "تصدير",
            }}
            sx={reportGridSx}
          />
        </ReportTableCard>
      </ReportPage>

      {/* قائمة إنشاء سند جديد */}
      <Menu
        anchorEl={newMenuAnchor}
        open={Boolean(newMenuAnchor)}
        onClose={() => setNewMenuAnchor(null)}
        PaperProps={{ sx: { borderRadius: "12px", mt: .5, minWidth: 210 } }}
        sx={{ zIndex: 1400 }}
      >
        {VOUCHER_TYPES.map(t => (
          <MenuItem key={t.value} onClick={() => { setNewMenuAnchor(null); navigate(`/vouchers/${t.route}`); }} sx={{ py: 1 }}>
            <ListItemIcon sx={{ color: t.fg, minWidth: 34 }}>{t.icon}</ListItemIcon>
            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{t.label}</Typography>
          </MenuItem>
        ))}
      </Menu>

      {/* تأكيد الحذف */}
      <Dialog open={Boolean(deleteTarget)} onClose={() => !deleting && setDeleteTarget(null)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: "16px" } }} dir="rtl">
        <DialogTitle sx={{ fontWeight: 800 }}>تأكيد حذف السند</DialogTitle>
        <DialogContent>
          <Typography>
            هل تريد حذف السند رقم <strong>{deleteTarget?.voucherNumber}</strong> بمبلغ{" "}
            <strong dir="ltr">{fmtMoney(deleteTarget?.amount)}</strong>؟
          </Typography>
          <Typography sx={{ mt: 1, fontSize: 13, color: "#be123c" }}>
            سيُحذف أثره على الخزينة أو البنك وحساب الطرف، ولا يمكن التراجع.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting} sx={{ fontWeight: 700 }}>إلغاء</Button>
          <Button onClick={handleDelete} color="error" variant="contained" disableElevation disabled={deleting}
            sx={{ fontWeight: 700, borderRadius: "10px" }}>
            {deleting ? "جاري الحذف..." : "حذف"}
          </Button>
        </DialogActions>
      </Dialog>

      <VoucherPrintDialog
        open={Boolean(printVoucher)}
        onClose={() => setPrintVoucher(null)}
        voucher={printVoucher}
        companyInfo={companyInfo}
      />
    </>
  );
}